import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Chart, registerables } from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './Dashboard.css';

const MetricCard = ({ label, value, onClick, accentClass }) => {
  const getIcon = (lbl) => {
    const key = (lbl || '').toLowerCase();
    if (key.includes('facebook')) return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M18 2h-3a4 4 0 00-4 4v3H8v4h3v8h4v-8h3l1-4h-4V6a1 1 0 011-1h2V2z" /></svg>
    );
    if (key.includes('instagram')) return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="3" /><circle cx="17.5" cy="6.5" r="0.6" stroke="currentColor" fill="none" /></svg>
    );
    if (key.includes('tiktok')) return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 18V8l7-2v10" />
        <circle cx="17" cy="16" r="2" stroke="currentColor" fill="none" />
      </svg>
    );
    if (key.includes('units') || key.includes('available') || key.includes('project')) return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><path d="M3 7l9-4 9 4v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" /><path d="M9 21V10" /><path d="M15 21V10" /></svg>
    );
    if (key.includes('inbound') || key.includes('calls') || key.includes('phone')) return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 16.92v3a2 2 0 01-2 2 19.8 19.8 0 01-8.63-3.07A19.5 19.5 0 012 6.08 19.8 19.8 0 015.07 2 2 2 0 017 2h3a2 2 0 012 1.72c.12 1.21.5 2.46 1.1 3.63" />
        <polyline points="16 3 21 3 21 8" />
      </svg>
    );
    // generic user/lead icons (man.svg style)
    if (key.includes('lead') || key.includes('leads') || key.includes('qualified') || key.includes('assigned') || key.includes('unassigned')) return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="8" r="3" /><path d="M5 20a7 7 0 0114 0" /></svg>
    );
    return (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="9" /></svg>
    );
  };

  return (
    <div className={`metric-card ${accentClass} clickable`} onClick={onClick}>
      <div className="metric-top">
        <div className="metric-icon" aria-hidden="true">{getIcon(label)}</div>
        <div className="metric-label">{label}</div>
      </div>
      <div className="metric-value">{value}</div>
    </div>
  );
};

Chart.register(...registerables);

const sampleSourceData = [
  { label: 'Facebook', value: 120 },
  { label: 'Instagram', value: 95 },
  { label: 'Website', value: 80 },
  { label: 'Referral', value: 45 },
  { label: 'Email', value: 30 }
];

const sampleMonthData = Array.from({ length: 12 }).map((_, i) => ({ label: new Date(0, i).toLocaleString('default', { month: 'short' }), value: Math.floor(Math.random() * 120) }));

const Dashboard = () => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const ranges = [
    'Today','Yesterday','Last 7 Days','Last 30 Days','This Month','Last Month','Last 6 Month','Last 3 Month','This Year','Custom Range'
  ];
  const [activeRange, setActiveRange] = useState('Yesterday');
  const [showRanges, setShowRanges] = useState(false);
  const filterRef = useRef(null);
  const datePickerRef = useRef(null);
  const [metrics, setMetrics] = useState([
    { label: 'Leads Created', value: null, accentClass: 'accent-coral' },
    { label: 'Qualified Leads', value: null, accentClass: 'accent-indigo' },
    { label: 'Unassigned Leads', value: null, accentClass: 'accent-amber' },
    { label: 'Assigned Leads', value: null, accentClass: 'accent-teal' },
    { label: 'Units Available', value: null, accentClass: 'accent-emerald' },
    { label: 'Facebook Leads', value: null, accentClass: 'accent-blue' },
    { label: 'Instagram Leads', value: null, accentClass: 'accent-magenta' },
    { label: 'TikTok Leads', value: null, accentClass: 'accent-orange' },
    { label: 'Inbound Calls', value: null, accentClass: 'accent-yellow' },
    { label: 'Other Leads', value: null, accentClass: 'accent-slate' },
  ]);
  
  // store Chart instances so we can destroy before re-creating
  const chartsMap = useRef({});

  useEffect(() => {
    // initial render of sample charts
    renderBarChart('chartSources', sampleSourceData, 'Leads');
    renderLineChart('chartMonths', sampleMonthData, 'Leads');
    // auto-fetch dashboard data from central API instead of using localStorage endpoint
    (async () => {
      try {
        const resp = await axios.post(
          'https://api.apps.rabahrain.com/api/crm/crmdashboard/main',
          {},
          {
            headers: {
              accept: '*/*',
              Authorization: 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJBYmR1bFJhaG1hbiIsImp0aSI6IjZiMmQxYjMzLWEzODgtNDRiNy04NmVmLTg0ODZlZWRmYjNjYyIsImVtYWlsIjoicmFobWFuQHJhYmFocmFpbi5jb20iLCJ1c2VySUQiOiIyODY4IiwidXNlck5vIjoiMTM5NyIsInVzZXJOYW1lIjoiQWJkdWxSYWhtYW4iLCJ1c2VyRnVsbE5hbWUiOiJBYmR1bHJhaG1hbiBBbC5RYXNzaW1pIiwidXNlckVtYWlsIjoicmFobWFuQHJhYmFocmFpbi5jb20iLCJ1c2VyUm9sZSI6IlNhbGVzQWRtaW4iLCJ1c2VyQ29udGFjdCI6IjY2NzA5MzYzIiwiY29tcGFueUlkIjoiMTAxMjYiLCJjb21wYW55Tm8iOiIxMTA1Iiwicm9sZXMiOiJTYWxlc0FkbWluIiwiZXhwIjoxNzY5NTE4MTM2LCJpc3MiOiJJbnZlbnRvcnlBdXRoZW50aWNhdGlvblNlcnZlciIsImF1ZCI6IkludmVudG9yeVNlcnZpY2VQb3N0bWFuQ2xpZW50In0.RWvp21kA0Bu8mLh5O8BDLRd2Dzs4LvX-MklfYvMlpRg',
              db: 'ePMS1106',
              'Content-Type': 'application/json'
            }
          }
        );
        console.log('dashboard auto-fetch response', resp);
        const payload = (resp && resp.data && (resp.data.data && resp.data.data.data)) ? resp.data.data.data : (resp && resp.data && resp.data.data) ? resp.data.data : (resp && resp.data) ? resp.data : {};
        const summary = payload.summary || {};
        const normalize = s => ('' + s).toLowerCase().replace(/[^a-z0-9]/g, '');
        const normSummary = Object.keys(summary || {}).reduce((acc, k) => { acc[normalize(k)] = summary[k]; return acc; }, {});
        const labelToKey = {
          'Leads Created': 'leadsCreated',
          'Qualified Leads': 'qualifiedLeads',
          'Unassigned Leads': 'unassignedLeads',
          'Assigned Leads': 'assignedLeads',
          'Units Available': 'unitsAvailable',
          'Facebook Leads': 'facebookLeads',
          'Instagram Leads': 'instagramLeads',
          'TikTok Leads': 'tikTokLeads',
          'Inbound Calls': 'inboundCalls',
          'Other Leads': 'otherLeads'
        };
        const getSummaryValue = (lbl) => {
          const directKey = labelToKey[lbl];
          if (directKey && summary[directKey] !== undefined && summary[directKey] !== null) return summary[directKey];
          const n = normalize(lbl);
          if (normSummary[n] !== undefined && normSummary[n] !== null) return normSummary[n];
          // try removing words like leads, calls, available
          const fallback = n.replace(/leads|calls|available|units|leadcount/g, '');
          if (normSummary[fallback] !== undefined && normSummary[fallback] !== null) return normSummary[fallback];
          return 0;
        };
        console.log('dashboard payload summary', summary);
        setMetrics(prev => prev.map(m => ({ ...m, value: getSummaryValue(m.label) })));
        // render charts if provided, otherwise render empty charts
        renderBarChart('chartSources', payload.sources && payload.sources.length ? [...payload.sources].reverse() : [], 'Leads');
        renderLineChart('chartMonths', payload.months && payload.months.length ? payload.months : [], 'Leads');
        renderBarChart('chartAssigned', payload.assigned && payload.assigned.length ? payload.assigned : [], 'Leads');
        renderBarChart('chartUnits', payload.units && payload.units.length ? payload.units : [], 'Units');
        renderBarChart('chartActivities', payload.activities && payload.activities.length ? payload.activities : [], 'Activities');
      } catch (e) {
        console.warn('Auto-fetch failed for central API', e);
        // on error set zeros and empty charts
        setMetrics(prev => prev.map(m => ({ ...m, value: 0 })));
        renderBarChart('chartSources', [], 'Leads');
        renderLineChart('chartMonths', [], 'Leads');
      }
    })();
  }, []);

  useEffect(() => {
    // re-render charts on metrics change if needed
  }, [metrics]);

  const renderBarChart = (canvasId, items, label) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const safeItems = items && items.length ? items : [{ label: 'No data', value: 0 }];
    const labels = safeItems.map(i => i.label);
    const data = safeItems.map(i => i.value);
    // destroy previous chart instance on this canvas if exists
    try {
      if (chartsMap.current[canvasId]) chartsMap.current[canvasId].destroy();
    } catch (e) {
      console.warn('Error destroying chart (renderBarChart)', e);
    }
    const softColors = ['#93c5fd','#a78bfa','#f0abfc','#fbcfe8','#fde68a','#bbf7d0','#fcd5ce','#c7d2fe','#e9d5ff','#fef3c7'];
    const assignedColors = ['#e6f6f5','#cfeef0','#b7e6eb','#9fdede','#87d6d3','#6ecfd1'];
    const palette = (canvasId === 'chartAssigned') ? assignedColors : softColors;
    // For Top Sources use horizontal bars so labels show fully
    const isSources = canvasId === 'chartSources';
    const config = {
      type: 'bar',
      data: { labels, datasets: [{ label, data, backgroundColor: labels.map((_, i) => palette[i % palette.length]) }] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: isSources ? 'y' : 'x',
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true }
        },
        scales: isSources ? {
          x: {
            ticks: { display: true, color: '#475569', beginAtZero: true },
            grid: { color: 'rgba(148,163,184,0.12)' }
          },
          y: {
            ticks: { display: true, color: '#475569', autoSkip: false },
            grid: { display: false }
          }
        } : {
          x: {
            ticks: { display: true, color: '#475569', maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
            grid: { display: false }
          },
          y: {
            ticks: { display: true, color: '#475569' , beginAtZero: true},
            grid: { color: 'rgba(148,163,184,0.12)' },
            beginAtZero: true
          }
        }
      }
    };

    const chart = new Chart(canvas, config);
    chartsMap.current[canvasId] = chart;
  };

  const renderLineChart = (canvasId, items, label) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const safeItems = items && items.length ? items : [{ label: 'No data', value: 0 }];
    const labels = safeItems.map(i => i.label);
    const data = safeItems.map(i => i.value);
    try {
      if (chartsMap.current[canvasId]) chartsMap.current[canvasId].destroy();
    } catch (e) {
      console.warn('Error destroying chart (renderLineChart)', e);
    }
    const chart = new Chart(canvas, {
      type: 'line',
      data: { labels, datasets: [{ label, data, borderColor: '#7c3aed', backgroundColor: 'rgba(124,58,237,0.08)', fill: true, pointBackgroundColor: '#a78bfa' }] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: true } },
        scales: {
          x: {
            ticks: { display: true, color: '#475569', maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
            grid: { display: false }
          },
          y: {
            ticks: { display: true, color: '#475569' },
            grid: { color: 'rgba(148,163,184,0.12)' },
            beginAtZero: true
          }
        }
      }
    });
    chartsMap.current[canvasId] = chart;
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(chartsMap.current).forEach(c => {
        try { c.destroy(); } catch (e) { console.warn('Error destroying chart instance on cleanup', e); }
      });
      chartsMap.current = {};
    };
  }, []);

  const [loading, setLoading] = useState(false);

  const handleDateChange = (update) => {
    setDateRange(update);
  };

  const applyRange = (key) => {
    setShowRanges(false);
    
    if (key === 'Custom Range') {
      // Clear dates and open datepicker calendar for manual range pick
      setActiveRange(key);
      setDateRange([null, null]);
      try { if (datePickerRef.current && typeof datePickerRef.current.setOpen === 'function') datePickerRef.current.setOpen(true); } catch (e) { console.warn('Error opening datepicker calendar', e); }
      return;
    }

    // For preset ranges, calculate dates and populate datepicker
    setActiveRange(key);
    const now = new Date();
    let from = null; let to = null;
    const startOfDay = d => { const r = new Date(d); r.setHours(0,0,0,0); return r; };
    const endOfDay = d => { const r = new Date(d); r.setHours(23,59,59,999); return r; };
    
    if (key === 'Today') { from = startOfDay(now); to = endOfDay(now); }
    else if (key === 'Yesterday') { const y = new Date(now); y.setDate(y.getDate()-1); from = startOfDay(y); to = endOfDay(y); }
    else if (key === 'Last 7 Days') { const s = new Date(now); s.setDate(s.getDate()-6); from = startOfDay(s); to = endOfDay(now); }
    else if (key === 'Last 30 Days') { const s = new Date(now); s.setDate(s.getDate()-29); from = startOfDay(s); to = endOfDay(now); }
    else if (key === 'This Month') { const s = new Date(now.getFullYear(), now.getMonth(), 1); from = startOfDay(s); to = endOfDay(now); }
    else if (key === 'Last Month') { const s = new Date(now.getFullYear(), now.getMonth()-1, 1); const e = new Date(now.getFullYear(), now.getMonth(), 0); from = startOfDay(s); to = endOfDay(e); }
    else if (key === 'Last 6 Month') { const s = new Date(now.getFullYear(), now.getMonth()-5, 1); from = startOfDay(s); to = endOfDay(now); }
    else if (key === 'Last 3 Month') { const s = new Date(now.getFullYear(), now.getMonth()-2, 1); from = startOfDay(s); to = endOfDay(now); }
    else if (key === 'This Year') { const s = new Date(now.getFullYear(), 0, 1); from = startOfDay(s); to = endOfDay(now); }

    setDateRange([from, to]);
    // auto-fetch after range selection
    setTimeout(() => handleFetchFromApi(), 80);
  };

  useEffect(() => {
    // apply default selected range on mount
    if (activeRange) applyRange(activeRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!filterRef.current) return;
      if (!filterRef.current.contains(e.target)) setShowRanges(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleFetchFromApi = async () => {
    setLoading(true);
    try {
      const requestBody = (startDate && endDate) ? {
        fromDate: (new Date(startDate)).toISOString(),
        toDate: (new Date(endDate)).toISOString(),
        userName: 'admin',
        top: 0,
        months: 0
      } : {};

      const resp = await axios.post(
        'https://api.apps.rabahrain.com/api/crm/crmdashboard/main',
        requestBody,
        {
          headers: {
            accept: '*/*',
            Authorization: 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJBYmR1bFJhaG1hbiIsImp0aSI6IjZiMmQxYjMzLWEzODgtNDRiNy04NmVmLTg0ODZlZWRmYjNjYyIsImVtYWlsIjoicmFobWFuQHJhYmFocmFpbi5jb20iLCJ1c2VySUQiOiIyODY4IiwidXNlck5vIjoiMTM5NyIsInVzZXJOYW1lIjoiQWJkdWxSYWhtYW4iLCJ1c2VyRnVsbE5hbWUiOiJBYmR1bHJhaG1hbiBBbC5RYXNzaW1pIiwidXNlckVtYW1pIjoicmFobWFuQHJhYmFocmFpbi5jb20iLCJ1c2VyUm9sZSI6IlNhbGVzQWRtaW4iLCJ1c2VyQ29udGFjdCI6IjY2NzA5MzYzIiwiY29tcGFueUlkIjoiMTAxMjYiLCJjb21wYW55Tm8iOiIxMTA1Iiwicm9sZXMiOiJTYWxlc0FkbWluIiwiZXhwIjoxNzY5NTE4MTM2LCJpc3MiOiJJbnZlbnRvcnlBdXRoZW50aWNhdGlvblNlcnZlciIsImF1ZCI6IkludmVudG9yeVNlcnZpY2VQb3N0bWFuQ2xpZW50In0.RWvp21kA0Bu8mLh5O8BDLRd2Dzs4LvX-MklfYvMlpRg',
            db: 'ePMS1106',
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('dashboard manual fetch response', resp);
      const payload = (resp && resp.data && (resp.data.data && resp.data.data.data)) ? resp.data.data.data : (resp && resp.data && resp.data.data) ? resp.data.data : (resp && resp.data) ? resp.data : {};
      const summary = payload.summary || {};
      const normalize = s => ('' + s).toLowerCase().replace(/[^a-z0-9]/g, '');
      const normSummary = Object.keys(summary || {}).reduce((acc, k) => { acc[normalize(k)] = summary[k]; return acc; }, {});
      const labelToKey = {
        'Leads Created': 'leadsCreated',
        'Qualified Leads': 'qualifiedLeads',
        'Unassigned Leads': 'unassignedLeads',
        'Assigned Leads': 'assignedLeads',
        'Units Available': 'unitsAvailable',
        'Facebook Leads': 'facebookLeads',
        'Instagram Leads': 'instagramLeads',
        'TikTok Leads': 'tikTokLeads',
        'Inbound Calls': 'inboundCalls',
        'Other Leads': 'otherLeads'
      };
      const getSummaryValue = (lbl) => {
        const directKey = labelToKey[lbl];
        if (directKey && summary[directKey] !== undefined && summary[directKey] !== null) return summary[directKey];
        const n = normalize(lbl);
        if (normSummary[n] !== undefined && normSummary[n] !== null) return normSummary[n];
        const fallback = n.replace(/leads|calls|available|units|leadcount/g, '');
        if (normSummary[fallback] !== undefined && normSummary[fallback] !== null) return normSummary[fallback];
        return 0;
      };
      setMetrics(prev => prev.map(m => ({ ...m, value: getSummaryValue(m.label) })));
      if (payload.sources) renderBarChart('chartSources', [...payload.sources].reverse(), 'Leads');
      if (payload.months) renderLineChart('chartMonths', payload.months, 'Leads');
      if (payload.assigned) renderBarChart('chartAssigned', payload.assigned, 'Leads');
      if (payload.units) renderBarChart('chartUnits', payload.units, 'Units');
      if (payload.activities) renderBarChart('chartActivities', payload.activities, 'Activities');
    } catch (e) {
      console.error('Fetch failed', e);
      alert('Failed to fetch from API (see console).');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <section className="dashboard-hero">
        <div className="hero-text">
          <h1>Dashboard</h1>
          <div className="filter-container">
            <div ref={filterRef} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
              <div style={{ flex: '1 1 auto' }} />
              <div className="date-filter-wrapper" style={{ position: 'relative' }} onClick={() => setShowRanges(true)} data-custom-range={activeRange === 'Custom Range'}>
                <i className="fa fa-calendar"></i>
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
                  ref={datePickerRef}
                  onInputClick={() => setShowRanges(true)}
                  onFocus={() => setShowRanges(true)}
                  placeholderText="Select Date Range"
                  className="form-control react-datepicker-ignore-onclickoutside"
                />
                <button className="btn-apply-filter" onClick={handleFetchFromApi} disabled={loading}>{loading ? 'Loading...' : 'Apply Filter'}</button>
                <ul className={"range-popup" + (showRanges ? ' open' : '')}>
                  {ranges.map(r => (
                    <li key={r} className={"range-tab" + (r === activeRange ? ' active' : '')} onMouseDown={(e) => { e.preventDefault(); applyRange(r); }}>{r}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="content dashboard-content">
        <div className="metrics-grid">
          {metrics.map((metric, index) => (
            <MetricCard
              key={index}
              label={metric.label}
              value={metric.value}
              accentClass={metric.accentClass}
              onClick={() => console.log(`${metric.label} clicked`)}
            />
          ))}
        </div>

        <div className="charts-grid">
          <div className="chart-card fullpage">
            <div className="chart-title">
              <div className="title-text">Top 10 Lead Sources</div>
              <div className="title-gradient"></div>
            </div>
            <canvas id="chartSources"></canvas>
          </div>
          <div className="chart-card">
            <div className="chart-title">
              <div className="title-text">Leads by Month (Last 12)</div>
              <div className="title-gradient"></div>
            </div>
            <canvas id="chartMonths"></canvas>
          </div>
          <div className="chart-card">
            <div className="chart-title">
              <div className="title-text">Leads by Assigned To (Top 10)</div>
              <div className="title-gradient"></div>
            </div>
            <canvas id="chartAssigned"></canvas>
          </div>
          <div className="chart-card">
            <div className="chart-title">
              <div className="title-text">Available Units per Project (Top 10)</div>
              <div className="title-gradient"></div>
            </div>
            <canvas id="chartUnits"></canvas>
          </div>
          <div className="chart-card">
            <div className="chart-title">
              <div className="title-text">Activities by Telesales (Top 10)</div>
              <div className="title-gradient"></div>
            </div>
            <canvas id="chartActivities"></canvas>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;